<template>
  <o-modal
    ref="modal"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.saveChanges'), type: 'confirm', callback: handleConfirm },
    ]"
    :valid="validate"
    modal-class="m-2 bg-gray-50 dark:bg-gray-900 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-md sm:w-full h-full max-h-lg"
    content-class="mx-auto max-w-lg h-full py-4 px-4 flex flex-col"
    :loading="loading"
  >
    <div v-if="serviceAutomation" class="text-left h-full flex flex-col">
      <div class="text-center">
        <h3 class="font-medium leading-6 text-md text-gray-700 dark:text-gray-200 mb-1">
          {{ $t('components.service.actions.editAutomation.title') }}
        </h3>

        <p class="text-sm leading-5 text-gray-600 dark:text-gray-300" v-html="$t('components.service.actions.editAutomation.info', { title: serviceAutomation.contentTemplate?.name })" />
      </div>

      <div class="bg-white dark:bg-gray-900 border-t border-b -mx-8 mb-4 mt-6 flex-grow h-10">
        <o-scroll class="py-4 px-8 h-full">
          <o-form ref="form" class="h-full">
            <div class="flex space-x-4 mb-6">
              <o-field-group class="w-full" :label="$t('components.service.actions.editAutomation.enableDisabled')">
                <toggle-field v-model="serviceAutomation.enabled" />
              </o-field-group>
            </div>

            <div class="flex space-x-4 mb-6">
              <o-field-group class="w-full" :label="$t('components.service.actions.editAutomation.sendViaEmail')">
                <toggle-field v-model="_channels" value="email" />
              </o-field-group>
            </div>

            <!-- SMS Warning Message -->
            <div v-if="!isSmsChannelAvailable" class="flex space-x-4 mb-6">
              <o-alert variant="warning" class="w-full" show>
                {{ $t('components.service.actions.editAutomation.smsBlankWarning') }}
              </o-alert>
            </div>

            <div class="flex space-x-4 mb-6">
              <o-field-group class="w-full" :label="$t('components.service.actions.editAutomation.sendViaSMS')" :info="$t('components.service.actions.editAutomation.smsRatesNote')" :disabled="!isSmsChannelAvailable">
                <toggle-field v-model="_channels" value="sms" :disabled="!isSmsChannelAvailable" />
              </o-field-group>
            </div>

            <div class="flex space-x-4 mb-6">
              <o-field-group class="w-full" :label="$t('general.frequency')" :info="$t('contentTemplate.serviceAutomation.frequencyInfo')">
                <select-field v-model="serviceAutomation.frequency" :options="serviceAutomationFrequencyOptions" />
              </o-field-group>
            </div>

            <div class="flex space-x-4 mb-6">
              <o-field-group class="w-full" :label="$t('service.requestMode')" :info="$t('service.requestModeInfo')">
                <select-field v-model="serviceAutomation.requirementMode" :options="serviceAutomationRequirementModeOptions" />
              </o-field-group>
            </div>

            <o-field-group
              v-if="isRequirementModeScheduled"
              v-slot="{ status }"
              :label="$t('service.scheduledTime')"
              :name="$t('service.scheduledTime')"
              :info="$t('contentTemplate.serviceAutomation.scheduledTimeInfo')"
              class="mb-4 block"
              rules="required"
            >
              <div class="flex items-center space-x-2">
                <number-field
                  v-model="serviceAutomation.scheduleOffsetValue"
                  type="number"
                  :status="status"
                  class="w-1/2"
                />
                <segment-field v-model="serviceAutomation.scheduleOffsetMode" :options="scheduleOffsetOptions" class="border w-1/2" />
              </div>
            </o-field-group>
          </o-form>
        </o-scroll>
      </div>
    </div>
  </o-modal>
</template>

<script setup lang="ts">
import { OAlert, OFieldGroup, OForm } from '@/components/elements'
import { TextField, SelectField, NumberField, SegmentField, ToggleField } from '@/components/fields'

import {
  ServiceAutomationFrequency,
  ServiceAutomationFrequencyLabels,
  ServiceAutomationRequirementMode,
  ServiceAutomationRequirementModeLabels,
} from '~/_api/models/serviceAutomation'
import type { ServiceAutomation } from '~/_api/models/serviceAutomation'
import type { Service } from '~/_api/models/service'

const props = defineProps<{
  service: Service
}>()

const emit = defineEmits<{
  success: []
  error: []
  'update:service': [service: Service]
}>()

const { $api, $notification, $i18n } = useNuxtApp()
const $t = $i18n.t.bind($i18n)

const modal = ref()
const form = ref()
const serviceAutomation = ref<ServiceAutomation | null>(null)
const loading = ref(false)

const _channels = computed({
  get: () => serviceAutomation.value?.channels ?? ['email'],
  set: (value) => {
    if (serviceAutomation.value) {
      serviceAutomation.value.channels = value
    }
  },
})

const serviceAutomationFrequencyOptions = computed(() => {
  return Object.values(ServiceAutomationFrequency)
    .filter((value): value is ServiceAutomationFrequency => typeof value === 'number')
    .map((value) => ({
      label: $t(ServiceAutomationFrequencyLabels[value]),
      value,
    }))
})

const serviceAutomationRequirementModeOptions = computed(() => {
  return Object.values(ServiceAutomationRequirementMode)
    .filter((value): value is ServiceAutomationRequirementMode => typeof value === 'number')
    .map((value) => ({
      label: $t(ServiceAutomationRequirementModeLabels[value]),
      value,
    }))
})

const scheduleOffsetOptions = computed(() => [
  { label: $t('service.before'), value: 'before' },
  { label: $t('service.after'), value: 'after' },
])

const isRequirementModeScheduled = computed(() => {
  return serviceAutomation.value?.isRequirementModeScheduled
})

const isSmsChannelAvailable = computed(() => {
  return serviceAutomation.value?.availableChannels?.includes('sms') ?? false
})

const open = (automation: ServiceAutomation) => {
  serviceAutomation.value = automation
  modal.value?.open()
}

const validate = () => {
  return form.value?.validate()
}

const handleConfirm = async () => {
  loading.value = true

  try {
    const response = await $api.service.updateAutomation(props.service, serviceAutomation.value!)
    const service = response.data

    if (service) {
      $notification.toast({
        title: $t('components.service.actions.editAutomation.success'),
        text: $t('components.service.actions.editAutomation.successInfo', { title: serviceAutomation.value?.contentTemplate?.name }),
        variant: 'success',
      })

      emit('success')
      emit('update:service', service)

      loading.value = false
      return true
    }
  } catch (e) {
    $notification.toast({
      title: $t('components.service.actions.editAutomation.error'),
      text: $t('components.service.actions.editAutomation.errorInfo', { title: serviceAutomation.value?.contentTemplate?.name }),
    })

    loading.value = false
    emit('error')
  }
}

defineExpose({
  open,
})
</script>
